<script lang="ts">
  import type { Permission } from '@clinic/contracts';
  import type { Snippet } from 'svelte';
  import { createMeQuery } from '../shared/auth/auth.svelte';
  import { setOrg } from '../shared/auth/org.svelte';
  import { NO_SECTIONS, createSectionSettings } from '../shared/auth/sections';
  import { fa } from '../shared/i18n/fa';
  import { Alert, ErrorState, PulseLoader } from '../shared/ui';
  import { WelcomeGate } from '../features/welcome';
  import AppShell from './AppShell.svelte';

  /** Resolves /o/:orgId against the user's memberships and provides the org context to the pages. */
  let { orgId, children }: { orgId: string; children: Snippet } = $props();

  const me = createMeQuery();
  const membership = $derived(me.data?.memberships.find((m) => m.organization.id === orgId));
  const isClinic = $derived(membership?.organization.type === 'clinic');
  // The sections a clinic member reaches (ADR-036) shape every page, so they load with the org.
  const sectionSettings = createSectionSettings(
    () => orgId,
    () => !!orgId && isClinic,
  );
  const sections = $derived(sectionSettings.data?.mine ?? NO_SECTIONS);
  setOrg(() => ({
    orgId,
    membership: membership as NonNullable<typeof membership>,
    can: (p: Permission) => !!membership?.permissions.includes(p),
    sections,
    reaches: (kind, section) => sections[kind].includes(section),
    laserEnabled: sectionSettings.data?.laserEnabled ?? false,
  }));
</script>

{#if me.isPending || (isClinic && sectionSettings.isPending)}
  <PulseLoader page />
{:else if me.isError}
  <ErrorState error={me.error} onRetry={() => void me.refetch()} />
{:else if isClinic && sectionSettings.isError}
  <ErrorState error={sectionSettings.error} onRetry={() => void sectionSettings.refetch()} />
{:else if !membership}
  <main class="mx-auto max-w-md px-4 py-16">
    <Alert tone="error">{fa.org.notMember}</Alert>
  </main>
{:else}
  <AppShell>{@render children()}</AppShell>
  {#key orgId}<WelcomeGate />{/key}
{/if}
